/** @format */

import { IAppointmentType } from "lobbie";
import { useCallback, useMemo } from "react";
import SchedulingAppointmentTypeCard from "src/components/shared/scheduling/cards/SchedulingAppointmentTypeCard";
import { IS_MOBILE_PHONE } from "src/constants";
import { usePatientSelfSchedulingContext } from "src/hooks/patient_scheduling/usePatientSelfSchedulingContext";

import { isEmptyObject } from "src/utils";

const PatientSchedulingAppointmentType = () => {
    const { selfScheduleSettings, selfSchedule, setSelfSchedule, route, setRoute, getNextRoute } =
        usePatientSelfSchedulingContext();
    const handleSetSelected = useCallback(
        (newAppointmentType: IAppointmentType | null) => {
            if (!selfSchedule) return;
            setSelfSchedule({
                ...selfSchedule,
                appointmentType: newAppointmentType,
                location: selfSchedule?.location ? selfSchedule?.location : null,
            });
            setRoute(newAppointmentType !== null ? getNextRoute() : route);
        },
        [getNextRoute, route, selfSchedule, setRoute, setSelfSchedule],
    );

    const render = useMemo(() => {
        if (!selfSchedule) return [];
        const renderedIds = [] as number[];
        const types = [];
        const incrementor = IS_MOBILE_PHONE ? 1 : 2;
        for (
            let i = 0;
            i < (selfScheduleSettings?.appointmentTypes ?? []).length;
            i += incrementor
        ) {
            if (selfScheduleSettings?.appointmentTypes === undefined) continue;
            const type = selfScheduleSettings?.appointmentTypes[i];
            if (renderedIds.includes(type.id)) continue;

            if (IS_MOBILE_PHONE) {
                types.push(
                    <SchedulingAppointmentTypeCard
                        key={type.id}
                        appointmentType={type}
                        selected={selfSchedule?.appointmentType?.id === type.id}
                        setSelected={handleSetSelected}
                    />,
                );
                continue;
            }

            const nextType = selfScheduleSettings?.appointmentTypes[i + 1];
            types.push(
                <div key={type.id} className="row">
                    <div className="col-6">
                        <SchedulingAppointmentTypeCard
                            appointmentType={type}
                            selected={selfSchedule?.appointmentType?.id === type.id}
                            setSelected={handleSetSelected}
                        />
                    </div>
                    {nextType && (
                        <div className="col-6">
                            <SchedulingAppointmentTypeCard
                                appointmentType={nextType}
                                selected={selfSchedule?.appointmentType?.id === nextType.id}
                                setSelected={handleSetSelected}
                            />
                        </div>
                    )}
                </div>,
            );
        }
        return types;
    }, [selfSchedule, selfScheduleSettings?.appointmentTypes, handleSetSelected]);

    if (isEmptyObject(selfScheduleSettings?.appointmentTypes)) {
        const message = (() => {
            const locationName = selfSchedule?.location?.name
                ? ` at ${selfSchedule?.location.name}`
                : "";
            const locationPhone = selfSchedule?.location?.phoneNumber
                ? ` Please contact ${selfScheduleSettings?.accountName} at ${selfSchedule?.location?.phoneNumber} to schedule an ${selfScheduleSettings?.appointmentPatientFieldName}.`
                : "";

            return `Sorry, we could not find any ${selfScheduleSettings?.appointmentPatientFieldName} types${locationName} with availability through self-scheduling.${locationPhone}`;
        })();

        return (
            <div className="container-fluid">
                <h3>{`No ${selfScheduleSettings?.appointmentPatientFieldName} types found.`}</h3>
                <p>{message}</p>
            </div>
        );
    } else {
        return (
            <div className="container-fluid">
                <h3 className="extra-bold mt-4">
                    {"Select your"}
                    <br />
                    {`${selfScheduleSettings?.appointmentPatientFieldName} type:`}
                </h3>
                {render}
            </div>
        );
    }
};

export default PatientSchedulingAppointmentType;
